<script setup lang="ts">
import AppLayout from '@/Layouts/AppLayout.vue';
import InvoiceTable from '../Components/Common/Invoice/InvoiceTable.vue';
import { DocumentTextIcon, PlusIcon, CreditCardIcon } from '@heroicons/vue/16/solid';
import PageTitle from '@/Components/Common/PageTitle.vue';
import MainContainer from '@/packages/ui/src/MainContainer.vue';
import SecondaryButton from '@/packages/ui/src/Buttons/SecondaryButton.vue';
import PrimaryButton from '@/packages/ui/src/Buttons/PrimaryButton.vue';
import { router, Link } from '@inertiajs/vue3';
import { isAllowedToPerformPremiumAction } from '@/utils/billing';
import { canManageBilling } from '@/utils/permissions';
import UpgradeModal from '@/Components/Common/UpgradeModal.vue';
import { computed, ref } from 'vue';
import { useStorage } from '@vueuse/core';
import { useInvoicesQuery } from '../utils/useInvoicesQuery';
import InvoicesTabNavbar from '../Components/Common/Invoice/InvoicesTabNavbar.vue';
import InvoicesFilterDropdown from '../Components/Common/Invoice/InvoicesFilterDropdown.vue';
import InvoiceStatusFilterBadge from '../Components/Common/Invoice/InvoiceStatusFilterBadge.vue';
import type { InvoiceStatusFilter } from '../Components/Common/Invoice/InvoicesFilterDropdown.vue';
import type {
    SortColumn as InvoiceSortColumn,
    SortDirection as InvoiceSortDirection,
} from '../Components/Common/Invoice/InvoiceTable.vue';

const showUpgradeModal = ref(false);

interface InvoiceTableState {
    sortColumn: InvoiceSortColumn;
    sortDirection: InvoiceSortDirection;
    filters: {
        status: InvoiceStatusFilter;
    };
}

const invoiceTableState = useStorage<InvoiceTableState>('invoice-table-state', {
    sortColumn: 'reference',
    sortDirection: 'asc',
    filters: {
        status: 'all',
    },
});

const invoiceStatus = computed(() => {
    const status = invoiceTableState.value.filters.status;
    return status === 'all' ? undefined : status;
});
const { invoices } = useInvoicesQuery({ status: invoiceStatus });

function handleInvoiceSort(column: InvoiceSortColumn, direction: InvoiceSortDirection) {
    invoiceTableState.value.sortColumn = column;
    invoiceTableState.value.sortDirection = direction;
}

function removeInvoiceStatusFilter() {
    invoiceTableState.value.filters.status = 'all';
}

const handleCreate = () => {
    if (!isAllowedToPerformPremiumAction()) {
        showUpgradeModal.value = true;
        return;
    }
    router.visit('/invoices/create');
};
</script>

<template>
    <AppLayout title="Invoices" data-testid="invoices_view">
        <MainContainer
            class="py-5 border-b border-default-background-separator flex justify-between items-center">
            <div class="flex items-center space-x-6">
                <PageTitle :icon="DocumentTextIcon" title="Invoices"></PageTitle>
                <InvoicesTabNavbar active="invoices" />
            </div>
            <SecondaryButton :icon="PlusIcon" @click="handleCreate">
                Create Invoice
            </SecondaryButton>
        </MainContainer>
        <MainContainer v-if="isAllowedToPerformPremiumAction()">
            <div class="flex items-center gap-2 py-1">
                <InvoicesFilterDropdown
                    mode="invoices"
                    :invoice-status="invoiceTableState.filters.status"
                    recipient-status="all"
                    @update:invoice-status="invoiceTableState.filters.status = $event" />

                <InvoiceStatusFilterBadge
                    v-if="invoiceTableState.filters.status !== 'all'"
                    mode="invoices"
                    :value="invoiceTableState.filters.status"
                    @remove="removeInvoiceStatusFilter"
                    @update:value="invoiceTableState.filters.status = $event as InvoiceStatusFilter" />
            </div>
        </MainContainer>
        <div v-if="isAllowedToPerformPremiumAction()">
            <InvoiceTable
                :invoices="invoices"
                :sort-column="invoiceTableState.sortColumn"
                :sort-direction="invoiceTableState.sortDirection"
                @sort="handleInvoiceSort"></InvoiceTable>
        </div>
        <MainContainer v-else>
            <div class="flex flex-col items-center justify-center py-12">
                <div
                    class="rounded-full flex items-center justify-center w-20 h-20 mx-auto border border-border-tertiary bg-secondary mb-4">
                    <DocumentTextIcon class="w-12 text-text-secondary"></DocumentTextIcon>
                </div>
                <p class="text-lg text-text-primary font-semibold">
                    Invoicing is a premium feature
                </p>
                <p class="text-text-secondary mb-6">
                    Upgrade to a paid plan or start a trial to use this feature
                </p>
                <Link v-if="canManageBilling()" href="/billing">
                    <PrimaryButton :icon="CreditCardIcon"> Go to Billing </PrimaryButton>
                </Link>
            </div>
        </MainContainer>
        <UpgradeModal :show="showUpgradeModal" @close="showUpgradeModal = false">
            Invoicing is a premium feature that requires an active subscription or trial.
        </UpgradeModal>
    </AppLayout>
</template>
